/**
 * Newsletter sign-up form under the article (monfri ОБ-Р33) — or, on a company site, at the end of every page
 * (`placement: 'page'`, monfri ОБ-Р19 §5.3 screen 4: after the page's main content, else just before its footer).
 *
 * Self-gates on the Site Profile's `newsletter` block ({enabled, appId, pushUrl, language, color}, written by the CDP
 * when the owner switches the newsletter on in the setup guide). On an article page it puts one form right after the
 * article's text — the same element the news plugin measures the scroll on (selectors from the profile, else the
 * built-in ones), so a WordPress theme and most others need no setup. The form posts the address to Push
 * (POST {pushUrl}/api/v1/apps/{appId}/newsletter); Push sends the double opt-in e-mail and answers the same whatever
 * the address. Nothing is stored in the browser and nothing is tracked: it is the reader's own act.
 */
import { definePlugin } from '../base';
import type { CDPPlugin, CDPCoreAPI } from '../base';
import type { SiteProfile } from '../../core/types';
import { getArticleContentElement, isArticlePage } from '../news/detector';

type Lang = 'bg' | 'en';

const TEXTS: Record<Lang, { heading: string; text: string; placeholder: string; button: string; sending: string; done: string; invalid: string; error: string; fine: string }> = {
  bg: {
    heading: 'Бюлетинът ни',
    text: 'Най-четеното, направо в пощата ви.',
    placeholder: 'Вашият имейл',
    button: 'Абонирайте се',
    sending: 'Изпращане…',
    done: 'Проверете пощата си — пратихме ви писмо с бутон за потвърждение.',
    invalid: 'Проверете имейл адреса.',
    error: 'Не успяхме. Опитайте отново след малко.',
    fine: 'Ще получите писмо за потвърждение. Отписвате се с един клик от всяко писмо.',
  },
  en: {
    heading: 'Our newsletter',
    text: 'The most-read stories, straight to your inbox.',
    placeholder: 'Your e-mail',
    button: 'Subscribe',
    sending: 'Sending…',
    done: 'Check your inbox — we sent you an e-mail with a button to confirm.',
    invalid: 'Check the e-mail address.',
    error: 'That did not work. Please try again in a moment.',
    fine: 'You will get a confirmation e-mail. Unsubscribe with one click from any e-mail.',
  },
};

export interface NewsletterProfileBlock {
  enabled?: boolean;
  appId?: number;
  pushUrl?: string;
  language?: string;
  color?: string;
  /** 'article' (default): after the article's text; 'page': at the end of every page */
  placement?: 'article' | 'page';
  /** the owner's own heading and line (a company site's), else the built-in ones */
  heading?: string;
  text?: string;
}

/** Text for innerHTML: the profile's heading and line are the owner's words, never markup. */
export function escapeHtml(s: string): string {
  return s.replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c] as string);
}

/**
 * Where the form goes on a company site: after the page's main content (<main>, the WordPress entry), else just before
 * its footer, else at the end of the body. Returns the node to insert before and its parent.
 */
export function pageSpot(doc: Document): { parent: Node; before: Node | null } | null {
  const main = doc.querySelector('main, [role="main"], .entry-content, .site-content, #content');
  if (main) return { parent: main, before: null };
  const footer = doc.querySelector('footer, [role="contentinfo"]');
  if (footer && footer.parentNode) return { parent: footer.parentNode, before: footer };
  return doc.body ? { parent: doc.body, before: null } : null;
}

/** The form's language: the block's, else the page's, else Bulgarian. */
export function newsletterLanguage(block: NewsletterProfileBlock | undefined, pageLang: string | null | undefined): Lang {
  const l = (block?.language || pageLang || 'bg').toLowerCase().slice(0, 2);
  return l === 'en' ? 'en' : 'bg';
}

/** A colour the form may use for its button: #rgb or #rrggbb only. */
export function safeColor(c: string | undefined): string {
  return c && /^#[0-9a-f]{3}([0-9a-f]{3})?$/i.test(c) ? c : '#111111';
}

export const NEWSLETTER_HOST_ATTR = 'data-monfri-newsletter';

export function createNewsletterPlugin(): CDPPlugin {
  let core: CDPCoreAPI;

  function block(): NewsletterProfileBlock | undefined {
    const p = core.getSiteProfile() as SiteProfile & { newsletter?: NewsletterProfileBlock };
    const b = p.newsletter;
    return b && b.enabled && b.appId ? b : undefined;
  }

  function place(): void {
    const b = block();
    if (!b) return;
    if (document.querySelector(`[${NEWSLETTER_HOST_ATTR}]`)) return; // once per page
    const profile = core.getSiteProfile() as SiteProfile;
    const selectors = (profile.selectors || {}) as Record<string, string>;
    let insert: (host: HTMLElement) => void;
    if (b.placement === 'page') {
      const spot = pageSpot(document);
      if (!spot) return;
      insert = (host) => { spot.parent.insertBefore(host, spot.before); };
    } else {
      if (!isArticlePage(selectors)) return;
      const content = getArticleContentElement(selectors);
      if (!content || !content.parentNode) return;
      insert = (host) => { content.parentNode!.insertBefore(host, content.nextSibling); };
    }

    const lang = newsletterLanguage(b, document.documentElement.lang);
    const t = { ...TEXTS[lang] };
    if (typeof b.heading === 'string' && b.heading.trim()) t.heading = escapeHtml(b.heading.trim().slice(0, 80));
    if (typeof b.text === 'string' && b.text.trim()) t.text = escapeHtml(b.text.trim().slice(0, 200));
    const color = safeColor(b.color);
    const pushUrl = (b.pushUrl || 'https://push.monfri.net').replace(/\/+$/, '');

    const host = document.createElement('div');
    host.setAttribute(NEWSLETTER_HOST_ATTR, '1');
    const root = host.attachShadow ? host.attachShadow({ mode: 'open' }) : host;
    root.innerHTML = `
      <style>
        :host{display:block;margin:28px 0}
        .w{border:1px solid #e5e7eb;border-radius:10px;padding:18px 20px;font:inherit;color:inherit;background:transparent}
        h3{margin:0 0 4px;font-size:1.1em;font-weight:700}
        p{margin:0 0 12px;opacity:.8}
        form{display:flex;flex-wrap:wrap;gap:8px}
        input[type=email]{flex:1 1 220px;min-width:0;min-height:42px;padding:0 12px;border:1px solid #d1d5db;border-radius:8px;font:inherit;color:#111;background:#fff}
        button{min-height:42px;padding:0 18px;border:0;border-radius:8px;background:${color};color:#fff;font:inherit;font-weight:700;cursor:pointer}
        button[disabled]{opacity:.6;cursor:default}
        .hp{position:absolute;left:-9999px;width:1px;height:1px;overflow:hidden}
        small{display:block;margin-top:8px;font-size:.8em;opacity:.7}
        .msg{margin-top:10px;font-weight:600}
      </style>
      <div class="w" role="region" aria-label="${t.heading}">
        <h3>${t.heading}</h3>
        <p>${t.text}</p>
        <form novalidate>
          <input type="email" name="email" autocomplete="email" required placeholder="${t.placeholder}" aria-label="${t.placeholder}">
          <span class="hp" aria-hidden="true"><input type="text" name="website" tabindex="-1" autocomplete="off"></span>
          <button type="submit">${t.button}</button>
        </form>
        <small>${t.fine}</small>
        <div class="msg" role="status" aria-live="polite"></div>
      </div>`;

    const form = root.querySelector('form') as HTMLFormElement;
    const input = root.querySelector('input[type=email]') as HTMLInputElement;
    const trap = root.querySelector('input[name=website]') as HTMLInputElement;
    const button = root.querySelector('button') as HTMLButtonElement;
    const msg = root.querySelector('.msg') as HTMLElement;

    form.addEventListener('submit', (e) => {
      e.preventDefault();
      const email = (input.value || '').trim();
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) { msg.textContent = t.invalid; return; }
      button.disabled = true;
      button.textContent = t.sending;
      msg.textContent = '';
      fetch(`${pushUrl}/api/v1/apps/${encodeURIComponent(String(b.appId))}/newsletter`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ email, website: trap.value || '', language: lang }),
        credentials: 'omit',
      }).then((r) => {
        if (r.status === 202) {
          form.style.display = 'none';
          msg.textContent = t.done;
          return;
        }
        msg.textContent = r.status === 422 ? t.invalid : t.error;
        button.disabled = false;
        button.textContent = t.button;
      }).catch(() => {
        msg.textContent = t.error;
        button.disabled = false;
        button.textContent = t.button;
      });
    });

    insert(host);
  }

  return definePlugin({
    name: 'newsletter',
    version: '1.0.0',
    config: {},
    install(c: CDPCoreAPI) {
      core = c;
    },
    onInit() {
      core.whenConfigReady().then(() => place()).catch(() => place());
    },
    onPageChange() {
      place();
    },
  });
}
